import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useCallback, useEffect, useState } from 'react';
import type { ISbStoryData } from 'storyblok';

import Drawer from '../../../components/Layout/Drawer';
import {
  GeneratedTaxType,
  useGetArtistsBySlugQuery,
} from '../../../types/generated';
import {
  ArtistStoryblok,
  ProductStoryblok,
  ProductV2Storyblok,
} from '../../../types/generated-storyblok';
import { ProductPurchaseDetails } from '../../../types/Purchasing';
import { useAnalyticsEmitter } from '../../../utils/analytics/emitter';
import { EnquiryFormScreen } from '../containers/EnquiryFormScreen';
import { ApiEnquiryGateway } from '../gateways/ApiEnquiryGateway';
import type { EnquirySource } from '../types/Enquiries';
import { EnquirySuccessScreen } from './EnquirySuccessScreen';

type Props = {
  shown: boolean;
  onClose: () => void;
  source: EnquirySource;
  product: ProductStoryblok | ProductV2Storyblok;
  productPrice?: number;
  productTitle: string;
  productPurchaseDetails?: ProductPurchaseDetails;
  taxType?: GeneratedTaxType;
};

const enquiryGateway = new ApiEnquiryGateway();

type Screen = 'FORM' | 'SUCCESS';

export const EnquiryDrawer: React.FC<Props> = ({
  onClose,
  product,
  productPrice,
  productPurchaseDetails,
  productTitle,
  shown,
  source,
}) => {
  const [screen, setScreen] = useState<Screen>('FORM');
  const [emitter] = useAnalyticsEmitter();
  const shouldReduceMotion = useReducedMotion();
  const animationProps = {
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: '-16px' },
    initial: { opacity: 0, x: '16px' },
    transition: { type: 'tween', duration: shouldReduceMotion ? 0 : 0.12 },
  };

  const artists = (
    product.component === 'product_v2' ? product.artists : product.productArtist
  )
    ?.filter((item) => typeof item.artist !== 'string')
    .map((item) => {
      return (item.artist as ISbStoryData<ArtistStoryblok>)?.slug;
    });

  const { data: internalArtistsData } = useGetArtistsBySlugQuery({
    variables: {
      slugs: artists.filter((slug) => Boolean(slug)),
    },
  });

  useEffect(() => {
    if (shown) {
      emitter('enquire step', 'viewed');
    }
  }, [shown]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClose = useCallback(() => {
    onClose();

    // Switch back to the form after the drawer is hidden
    setTimeout(() => {
      setScreen('FORM');
    }, 500);
  }, [onClose]);

  const handleSuccess = useCallback(() => {
    setScreen('SUCCESS');
    emitter('enquire step', 'success');
  }, [emitter]);

  const handleSubmit = useCallback(() => {
    emitter('enquire step', 'submit');
  }, [emitter]);

  const thumbnail = {
    filename:
      product.component === 'product_v2'
        ? (product as unknown as ProductV2Storyblok).ogImage.filename
        : product.thumbnail.filename,
    focus:
      product.component === 'product_v2'
        ? ((product as unknown as ProductV2Storyblok).ogImage?.focus ?? '')
        : (product.thumbnail?.focus ?? ''),
  };

  return (
    <Drawer
      onClose={handleClose}
      overlayOtherDrawers
      shown={shown}
      showNavigationBorder
    >
      <AnimatePresence exitBeforeEnter>
        {screen === 'FORM' ? (
          <motion.div key="form" {...animationProps}>
            <EnquiryFormScreen
              artists={
                internalArtistsData?.artistsBySlug.map((artist) => ({
                  name: artist.name,
                  internalId: artist.id,
                })) || []
              }
              enquiryGateway={enquiryGateway}
              location="private room drawer"
              onSubmit={handleSubmit}
              onSuccess={handleSuccess}
              product={{
                id:
                  product.component === 'product_v2'
                    ? [{ id: product.shopifyId }]
                    : [{ id: product.id[0].id || '' }],
                title: productTitle,
              }}
              productPrice={productPrice}
              productPurchaseDetails={productPurchaseDetails}
              source={source}
              thumbnail={thumbnail}
            />
          </motion.div>
        ) : null}

        {screen === 'SUCCESS' ? (
          <motion.div key="success" {...animationProps}>
            <EnquirySuccessScreen
              onClose={handleClose}
              product={product}
              productPrice={productPrice}
              productPurchaseDetails={productPurchaseDetails}
              thumbnail={thumbnail}
            />
          </motion.div>
        ) : null}
      </AnimatePresence>
    </Drawer>
  );
};
